import React, { useCallback } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import useMutation from "src/hooks/useMutation";
import { useNavigate } from "react-router";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AssetItemImage from "../../../aseet-list/AssetItemImage";
import AssetItemSkeleton from "../../../aseet-list/AssetItemSkeleton";
import AssetItemDescription from "../../../aseet-list/AssetItemDescription";
import useQueryParams from "src/hooks/useQueryParams";
import {
  ADD_SHIPMENT_ITEMS_MUTATION,
  CREATE_ASSET_MUTATION,
  CREATE_IMAGE_MUTATION,
} from "src/constants/graphql/mutations";
import { GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";
import { fetchAssetValues } from "src/utils/asset";

const styles = {
  button: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    border: 0,
    boxSizing: "border-box",
    borderBottom: "1px solid #DDDBDA",
    height: 100,
    textAlign: "left",
    width: "100%",
    overflow: "hidden",
    padding: "8px",
    "&:hover": {
      backgroundColor: "#f4f6f8",
    },
  },
  arrow: {
    fontSize: 18,
    color: "action.active",
    ml: 1,
    display: { xs: "none", sm: "block" },
  },
};

const ITEM_CUSTOM_FIELD_TEMPLATE_ID = "676";

const getSkuLibraryLabelSuffix = asset => {
  const customFieldValue = asset?.customFields?.find(
    field => String(field?.customFieldTemplate?.id) === ITEM_CUSTOM_FIELD_TEMPLATE_ID
  )?.value;
  const extraValues = [asset?.poNumber, customFieldValue].filter(Boolean);

  return extraValues.length > 0 ? ` | ${extraValues.join(" | ")}` : "";
};

function AssetListItem({
  displayItemId = true,
  order,
  asset,
  style,
  project,
  closeParam = "itemLookup",
  selected,
  onToggle,
  showCheckbox = true,
}) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const shipmentId = params.get("id");
  const [createAsset] = useMutation(CREATE_ASSET_MUTATION);
  const [createImageMutation] = useMutation(CREATE_IMAGE_MUTATION);
  const [addShipmentItem] = useMutation(ADD_SHIPMENT_ITEMS_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
  });

  const handleAddAsset = useCallback(async () => {
    let assetToUse = asset;

    // Library SKUs are catalog templates; receive a new Asset copy so stock never lands on a Sku
    if (project.referenceAssets || asset.type === "Sku") {
      // The new item starts empty and is stocked by this shipment; copying the
      // picked item's rack locations would count its pallets twice.
      const { assetId, barcode, attachments, rackLocations, ...assetInput } = fetchAssetValues(asset);
      const result = await createAsset({
        variables: {
          projectId: project.id,
          input: assetInput,
        },
      });
      assetToUse = result.data.createAsset.asset;

      if (asset.images && asset.images.length > 0) {
        try {
          await Promise.all(
            asset.images.map(image =>
              createImageMutation({
                variables: {
                  assetId: assetToUse.id,
                  url: image.url,
                },
              })
            )
          );
        } catch (imageError) {
          console.error("Error duplicating images:", imageError);
        }
      }
    }

    const response = await addShipmentItem({
      variables: {
        assetId: assetToUse.id,
        shipmentId,
        quantity: 0,
      },
    });

    const shipmentItemId = response.data.addShipmentItems.shipmentItem.id;
    const pathname = window.location.pathname;
    const params = new URLSearchParams(location.search);

    params.delete(closeParam);
    params.set("assetId", assetToUse.id);
    params.set("shipmentItemId", shipmentItemId);
    params.set("addShipmentItem", "true");
    navigate({ pathname, search: params.toString() });
  }, [asset, navigate, project, closeParam, createAsset, createImageMutation, addShipmentItem, shipmentId]);

  const handleClick = e => {
    if (onToggle) {
      onToggle(asset.id);
    } else {
      handleAddAsset();
    }
  };

  if (!asset) {
    return (
      <div style={style} data-id="assets-list">
        <AssetItemSkeleton />
      </div>
    );
  }

  return (
    <div style={style} data-id="assets-list">
      <ButtonBase fullWidth variant="outlined" sx={styles.button} onClick={handleClick}>
        {onToggle && showCheckbox && (
          <Checkbox checked={!!selected} size="small" sx={{ p: 0.5, mr: 1 }} disableRipple />
        )}
        <AssetItemImage images={asset.images} />
        <AssetItemDescription
          displayItemId={displayItemId}
          order={order}
          asset={asset}
          descriptionLines={3}
          labelSuffix={
            project.referenceAssets || project.importedAssets
              ? getSkuLibraryLabelSuffix(asset)
              : undefined
          }
        />
        {!onToggle && <ArrowForwardIosIcon sx={styles.arrow} />}
      </ButtonBase>
    </div>
  );
}

export default AssetListItem;
